import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Search, Mail, Phone, Users, TrendingUp, Download } from "lucide-react";
import { formatPrice } from "../../lib/format";

export default function CustomersAdmin() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers?q=${encodeURIComponent(q)}&limit=200`);
      setCustomers(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/admin/customers/export.csv?q=${encodeURIComponent(q)}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `goshi-customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${customers.length} customers`);
    } catch (e) { toast.error(formatErr(e)); } finally { setExporting(false); }
  };

  const totalLtv = customers.reduce((a, c) => a + (c.lifetime_value || 0), 0);
  const withOrders = customers.filter(c => c.orders_count > 0).length;

  return (
    <div className="space-y-8" data-testid="customers-admin">
      <div className="flex items-end justify-between flex-wrap gap-4 pb-5 border-b border-[rgba(244,244,240,0.05)]">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Atelier</p>
          <h2 className="font-serif text-4xl">Customers</h2>
          <p className="text-xs text-muted mt-2">All registered accounts, their contact info, and their lifetime spend.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <StatCard icon={Users} label="Registered" value={customers.length} />
          <StatCard icon={TrendingUp} label="With orders" value={withOrders} />
          <StatCard icon={TrendingUp} label="Total LTV" value={formatPrice(totalLtv)} />
          <button
            onClick={exportCsv}
            disabled={exporting || customers.length === 0}
            className="btn-outline !px-4 !py-2 text-[10px] flex items-center gap-2 self-end"
            data-testid="customers-export-csv-btn"
            title="Download customer list as CSV (opens in Excel)"
          >
            <Download size={12} strokeWidth={1.5} /> {exporting ? "Preparing…" : "Export CSV"}
          </button>
        </div>
      </div>

      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input
          type="search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by name, email or phone…"
          className="input-line w-full !pl-9"
          data-testid="customer-search"
        />
      </div>

      <div className="bg-[#141414] border border-[rgba(244,244,240,0.05)] overflow-hidden">
        {loading ? (
          <p className="text-muted text-sm p-6">Loading customers…</p>
        ) : customers.length === 0 ? (
          <p className="text-muted text-sm p-6">No customers found.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-muted border-b border-[rgba(244,244,240,0.05)]">
                <th className="text-left p-4 font-normal">Name</th>
                <th className="text-left p-4 font-normal hidden md:table-cell">Email</th>
                <th className="text-left p-4 font-normal hidden md:table-cell">Phone</th>
                <th className="text-right p-4 font-normal">Orders</th>
                <th className="text-right p-4 font-normal">Lifetime</th>
                <th className="text-right p-4 font-normal hidden md:table-cell">Joined</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => (
                <tr key={c.user_id} className="border-b border-[rgba(244,244,240,0.03)] hover:bg-[#1a1a1a]" data-testid={`customer-${c.user_id}`}>
                  <td className="p-4">
                    <p className="font-serif">{c.name || "—"}</p>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-muted md:hidden mt-1">{c.email}</p>
                    {c.phone && <p className="text-[10px] uppercase tracking-[0.15em] text-muted md:hidden mt-0.5">{c.phone}</p>}
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <p className="text-muted text-xs flex items-center gap-1.5"><Mail size={10} strokeWidth={1.5} /> <a href={`mailto:${c.email}`} className="hover:text-gold">{c.email}</a></p>
                  </td>
                  <td className="p-4 hidden md:table-cell" data-testid={`customer-phone-${c.user_id}`}>
                    {c.phone
                      ? <p className="text-muted text-xs flex items-center gap-1.5"><Phone size={10} strokeWidth={1.5} /> <a href={`tel:+91${c.phone}`} className="hover:text-gold">{c.phone}</a></p>
                      : <span className="text-muted/50 text-xs">—</span>}
                  </td>
                  <td className="p-4 text-right">{c.orders_count}</td>
                  <td className="p-4 text-right text-gold font-serif">{formatPrice(c.lifetime_value || 0)}</td>
                  <td className="p-4 text-right text-muted text-xs hidden md:table-cell">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#141414] px-4 py-3 border border-[rgba(244,244,240,0.06)] flex items-center gap-3">
      <Icon size={14} strokeWidth={1.5} className="text-gold" />
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-muted">{label}</p>
        <p className="font-serif text-xl leading-none mt-1">{value}</p>
      </div>
    </div>
  );
}
